import { useEffect, useRef, useState } from "react";
import type { StyledSelectModel } from "../../type/styledSelect";
import "./styledSelect.scss";

function StyledSelect<T extends string = string>({
  label,
  value,
  options,
  onChange,
  icon,
  disabled = false,
  className = "",
  variant = "light"
}: StyledSelectModel<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value) ?? options[0];

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (optionValue: T) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`styled-select-row styled-select-row--${variant} ${className}`.trim()}>
      {icon && (
        <div className="styled-select-row-icon">
          <i className={`bi ${icon}`} />
        </div>
      )}

      <div className="styled-select-row-label">{label}</div>

      <div className={`styled-select ${disabled ? "styled-select--disabled" : ""}`} ref={containerRef}>
        <button
          type="button"
          className={`styled-select__control ${isOpen ? "styled-select__control--open" : ""}`}
          onClick={() => !disabled && setIsOpen(prev => !prev)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="styled-select__value">{selected?.label}</span>
          <i className="bi bi-chevron-down styled-select__caret" aria-hidden="true" />
        </button>

        {isOpen && (
          <ul className="styled-select__menu" role="listbox">
            {options.map(option => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className={`styled-select__option ${option.value === value ? "styled-select__option--selected" : ""}`}
                onClick={() => handleSelect(option.value)}
              >
                {option.value === value && <i className="bi bi-check2 styled-select__check" aria-hidden="true" />}
                <span>{option.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default StyledSelect;
